//TO DO: in the next PR refactor and test this context
import {
	createContext,
	Dispatch,
	ReactNode,
	SetStateAction,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';

import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useIntl } from 'react-intl';
import { v4 as createUUID } from 'uuid';

import { CreateUIAnalyticsEvent, useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useAnalyticsClient } from '@/analytics/wac-analytics/analytics-client';
import {
	trackRecaptchaFailure,
	trackSiteActivationViewed,
	trackCrossFlowActivationPlanFetched,
	trackCrossFlowActivationPlanFailed,
	trackSignupStarted,
	trackSiteNameChangedFromRecommendedSiteName,
	trackSitePickerSubmit,
	trackSubmitSuccess,
} from '@/components/signup/utils/analytics';
import { getConsents } from '@/components/signup/utils/get-consents';
import { getSignupContext } from '@/components/signup/utils/get-signup-context';
import { isTestTenantByName } from '@/components/signup/utils/is-test-tenant-by-name';
import {
	ProductKey,
	mapProductKeyAndEditionToCcpProduct,
} from '@/components/signup/utils/products';
import { getRecaptcha } from '@/components/signup/utils/recaptcha';
import { redirectToAdminHubForShadowIt } from '@/components/signup/utils/redirect-to-admin-hub-for-shadow-it.ts';
import {
	getAdminPaymentLink,
	type PaymentDetailsView,
} from '@/components/signup/utils/redirect-to-admin-hub-payment-details';
import {
	operateWithSignupUFOLogging,
	trackAndCompleteUFOEvent,
	SignupGetUFOErrorType,
	SignupUFOLoggingActionTimeoutError,
} from '@/components/signup/utils/signup-ufo-deprecated';
import {
	ufoEventExistingSiteN2ESubmit,
	ufoEventGraphQLWebhookSetup,
	ufoEventNewSiteN2NSubmit,
} from '@/components/signup/utils/ufo-events';
import {
	UfoEventName,
	UfoExperienceStatus,
	UfoInteractionType,
	type UfoLabelStack,
} from '@/components/signup/utils/ufo.types';
import { getCrossFlowActivationUrl } from '@/components/signup/utils/url';
import {
	getAdminHubUpgradeProductUrl,
	getConfirmationPageUrl,
} from '@/components/signup/utils/url';
import {
	COHORTS,
	useCohort,
	useCrossflowEligibleSites,
	useUpgradableSites,
} from '@/components/user-context/helpers/cohorts';
import { useUserContext } from '@/components/user-context/user-context-provider';
import {
	IBxpWebStargateClient,
	useBxpWebStargateClient,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import {
	ExistingSite,
	JobName,
	Platform,
} from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client.types';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Cookie } from '@/utils/cookie';
import { useLocation } from '@/utils/location-provider';

import { ErrorType, useSiteNameValidation } from '../../hooks/use-site-name-validation';
import { type FormattedMessageProps, messages as validationMessages } from '../messages';

const { ABORTED, SUCCEEDED } = UfoExperienceStatus;
const { PRESS, TRANSITION } = UfoInteractionType;
const ufoBaseLogs: UfoLabelStack = [
	{ name: 'pkg:site-activation' },
	{ name: 'component:siteActivationContextProvider' },
];
export interface SiteActivationContext {
	activateSite: ({ privacyPolicy }: { privacyPolicy?: string }) => void;
	sites: ExistingSite[];
	isRequestInProgress: boolean;
	showSitePicker: boolean;
	email?: string;
	sitePickerValue: ExistingSite | undefined;
	setSitePickerValue: Dispatch<ExistingSite | undefined>;
	setSiteNameValid: Dispatch<SetStateAction<boolean>>;
	siteName: string;
	setSiteNameValue: Dispatch<SetStateAction<string>>;
	isSubmitting: boolean;
	hasSubmitted: boolean;
	setHasSubmitted: Dispatch<SetStateAction<boolean>>;
	validationMessageState: [
		FormattedMessageProps | undefined,
		Dispatch<SetStateAction<FormattedMessageProps | undefined>>,
	];
	setShowSitePicker: Dispatch<SetStateAction<boolean>>;
	recommendedSiteName: string;
	isValid?: boolean;
	isValidating: boolean;
	error: ErrorType;
	isWorkspaceProduct: boolean;
	selectedTransactionAccountId: string;
	setSelectedTransactionAccountId: Dispatch<SetStateAction<string>>;
	primaryCollectionProduct?: string;
	/** "payment" shows payment overlay; view "payment-required" includes adminPaymentLink (opens in new tab); "site-setup" shows form. */
	paymentDetailsView: PaymentDetailsView;
	setPaymentDetailsView: Dispatch<SetStateAction<PaymentDetailsView>>;
}

export const SiteActivationContext = createContext<SiteActivationContext | null>(null);

export const useSiteActivationContext = (): SiteActivationContext => {
	const context = useContext(SiteActivationContext);
	if (!context) {
		throw new Error('useSiteActivationContext must be used within a SiteActivationContextProvider');
	}
	return context;
};

async function getHasPaidTargetEditionRequiringConfirmationScreen({
	cloudId,
	productKey,
	stargateClient,
	signupSource,
	createAnalyticsEvent,
}: {
	cloudId?: string;
	productKey?: ProductKey;
	signupSource: string | null;
	stargateClient: IBxpWebStargateClient;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
}): Promise<boolean> {
	if (
		!cloudId ||
		!productKey ||
		signupSource === 'pricing' ||
		![
			ProductKey.CONFLUENCE,
			ProductKey.JIRA_SOFTWARE,
			ProductKey.JIRA_PRODUCT_DISCOVERY,
			ProductKey.JIRA_SERVICE_DESK,
			ProductKey.SERVICE_COLLECTION,
		].includes(productKey)
	)
		return false;

	try {
		trackCrossFlowActivationPlanFetched({
			createAnalyticsEvent,
			cloudId,
			productKey,
		});
		const activateProductCrossflowPlanResult = await stargateClient.activateProductCrossflowPlan({
			cloudId,
			productKey,
			jobName: JobName.WAC_SIGNUP,
			context: {
				sourceComponent: 'redirect',
				sourceContext: 'start',
				originProduct: 'wac',
			},
		});

		const activatePlanTaskContext = activateProductCrossflowPlanResult?.tasks?.find(
			(task) => task?.name === 'activate',
		)?.context;
		const plannedEditionId = (
			activatePlanTaskContext?.['edition'] as {
				id?: string;
			}
		)?.['id'];
		const plannedEditionStrategy = activatePlanTaskContext?.['editionStrategy'];

		return (
			(plannedEditionStrategy === 'highest' && plannedEditionId !== 'free') ||
			(plannedEditionStrategy === 'servco' && plannedEditionId === 'standard')
		);
	} catch (error) {
		trackCrossFlowActivationPlanFailed({
			createAnalyticsEvent,
			cloudId,
			productKey,
			error,
		});
		return false;
	}
}

export const SiteActivationContextProvider = ({
	children,
	existingSites,
	useContactExpandCohortLogic,
}: {
	children: ReactNode;
	existingSites?: ExistingSite[];
	useContactExpandCohortLogic?: boolean;
}) => {
	//HOOKS
	const intl = useIntl();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsClient = useAnalyticsClient();
	const {
		profile,
		user,
		product,
		edition: originalEdition = 'free',
		transactionAccounts,
	} = useUserContext();

	/**
	 * Rovo Dev (aka DEVAI) is a special case, it only has a single
	 * edition, standard. There is an outstanding DACI:
	 * https://hello.atlassian.net/wiki/spaces/uts/pages/5807772710/Rovo+Dev+Everywhere+SKU+DACI
	 * If/when this DACI is resolved and Rovo Dev supports the free
	 * edition, this special case can then be removed.
	 */
	let edition = originalEdition;
	if (product?.key === ProductKey.DEVAI) {
		edition = 'standard';
	}

	const { companyType, domainType, userType } = profile?.cohorts || {};
	const cohort = useCohort(useContactExpandCohortLogic);
	const expandableSites = useCrossflowEligibleSites();
	const upgradableSites = useUpgradableSites();
	const stargateClient = useBxpWebStargateClient();
	const location = useLocation();
	const sites = useMemo(
		() =>
			existingSites ??
			(cohort === COHORTS.UPGRADE_ELIGIBLE_USER ? upgradableSites : expandableSites) ??
			[],
		[cohort, expandableSites, upgradableSites, existingSites],
	);
	const isWorkspaceProduct = useMemo(
		() =>
			!!product?.key &&
			[
				ProductKey.CONFLUENCE,
				ProductKey.TEAMWORK_COLLECTION,
				ProductKey.SERVICE_COLLECTION,
			].includes(product?.key),
		[product?.key],
	);

	//STATE
	const [showSitePicker, setShowSitePicker] = useState(sites.length > 0);
	const [siteNameValid, setSiteNameValid] = useState(false);
	const [hasSubmitted, setHasSubmitted] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [sitePickerValue, setSitePickerValue] = useState<ExistingSite | undefined>(sites[0]);
	const [siteName, setSiteNameValue] = useState('');
	const [recommendedSiteName] = useState('');
	const [selectedTransactionAccountId, setSelectedTransactionAccountId] = useState<string>(
		transactionAccounts[0]?.id ?? '',
	);
	const [recaptchaFailedToLoad, setRecaptchFailedToLoad] = useState<boolean>(false);
	const [recaptchaRetryTimeRemainingSecond, setRecaptchaRetryTimeRemainingSecond] = useState(60);
	const [validatationMessage, setValidationMessage] = useState<undefined | FormattedMessageProps>();
	const [paymentDetailsView, setPaymentDetailsView] = useState<PaymentDetailsView>({
		view: 'site-setup',
	});

	//VARIABLES
	const searchParameters = new URLSearchParams(location.search);
	const platform = (searchParameters.get('platform') ?? 'routing') as Platform;
	const primaryCollectionProduct = searchParameters.get('primaryCollectionProduct') as
		| ProductKey
		| undefined;
	const localizedContactUrl = useLocalizedHref('/company/contact');
	const isNewUser = profile.cohorts?.userType === 'new-user';
	const productKey = product?.key as ProductKey;
	const { isValid, isValidating, error } = useSiteNameValidation(siteName, setSiteNameValid);
	const isEmbedded = !location.pathname?.endsWith('/try/cloud/signup');
	const dataFoundationsActionSubjectId = isEmbedded ? 'embedded' : 'standalone';

	const showRecaptchaFormError = useCallback(() => {
		const anonymousId = decodeURIComponent(Cookie.readCookie('ajs_anonymous_id') || 'n/a');
		setValidationMessage({
			...validationMessages.timeout,
			values: {
				// eslint-disable-next-line react/no-unstable-nested-components
				'href-support': (node: ReactNode[]) => <a href={localizedContactUrl}>{node}</a>,
				error_id: `Anonymous ID: ${anonymousId}`,
			},
		});

		// Reset form to allow re-submit
		setIsSubmitting(false);
		setHasSubmitted(false);
	}, [localizedContactUrl]);

	const activateSiteHandler = useCallback(
		async (signupId?: string, privacyPolicy?: string) => {
			setIsSubmitting(true);

			if (!executeRecaptcha && recaptchaRetryTimeRemainingSecond > 0) {
				// set trigger that will wait for Recaptcha retry logic
				setRecaptchFailedToLoad(true);
				return;
			}
			setRecaptchFailedToLoad(false);

			const localeData = await stargateClient.getConsentConfig();

			const testingPurposes = isTestTenantByName(siteName);
			const signupContext = getSignupContext({
				productKey,
				testingPurposes,
				signupId,
				search: location.search,
				locale: intl.locale,
			});

			const MAX_SIGNUP_CONTEXT_LENGTH = 8192;
			let signupContextString: string = JSON.stringify(signupContext);
			if (signupContextString.length > MAX_SIGNUP_CONTEXT_LENGTH) {
				signupContextString = '{}';
			}
			const ccpProducts = mapProductKeyAndEditionToCcpProduct({
				productKey,
				edition,
				primaryCollectionProduct,
			});

			const gRecaptchaResponse = await getRecaptcha(user['email'] || '', executeRecaptcha);

			ufoEventNewSiteN2NSubmit.setCustomData({
				gRecaptchaResponse: gRecaptchaResponse ?? 'none',
				executeRecaptchaExists: Boolean(executeRecaptcha),
				reactComponent: 'SiteActivationProvider',
			});

			// Handle missing reCAPTCHA response
			if (!gRecaptchaResponse) {
				trackRecaptchaFailure(createAnalyticsEvent, 'site-activation');
				showRecaptchaFormError();
				return;
			}

			// Set experiment metadata before starting UFO event for GraphQL webhook setup
			ufoEventGraphQLWebhookSetup.setCustomData({
				webPlatform: 'site-activation',
			});

			// get consents
			const consent = getConsents(
				localeData.locale ?? 'US-VA',
				localeData.localeRequiresMarketingCommunicationOptIn,
				privacyPolicy,
			);

			const response = await stargateClient.activateProduct({
				displayName: user['name'],
				email: user['email'] || '',
				ccpProducts: [ccpProducts],
				siteName,
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				useCcpPlatform: platform === 'ccp',
				consent,
				platform,
				signupContext: signupContextString,
				isNewUser: productKey === ProductKey.JIRA_SOFTWARE || isNewUser,
				gRecaptchaResponse,
				testingPurposes,
				signupId,
				transactionAccountId: selectedTransactionAccountId,
			});

			const redirectUrl = getConfirmationPageUrl({
				locale: intl.locale,
				productKey,
				response,
				siteName,
				signupId,
				edition,
				primaryCollectionProduct,
			});
			window.location.assign(redirectUrl);
		},
		[
			executeRecaptcha,
			recaptchaRetryTimeRemainingSecond,
			showRecaptchaFormError,
			stargateClient,
			siteName,
			primaryCollectionProduct,
			location.search,
			productKey,
			edition,
			user,
			platform,
			isNewUser,
			intl.locale,
			createAnalyticsEvent,
			selectedTransactionAccountId,
		],
	);

	useEffect(() => {
		if (profile.requestInProgress || !analyticsClient) return;
		trackSiteActivationViewed(createAnalyticsEvent, {
			actionSubjectId: dataFoundationsActionSubjectId,
			cohort,
			productKey,
			edition,
			userType,
			domainType,
			companyType,
			numTxas: transactionAccounts.length,
		});
	}, [
		analyticsClient,
		cohort,
		createAnalyticsEvent,
		companyType,
		dataFoundationsActionSubjectId,
		domainType,
		edition,
		productKey,
		profile.requestInProgress,
		userType,
		transactionAccounts.length,
	]);

	useEffect(() => {
		const ufoSubmitEvent = showSitePicker
			? ufoEventExistingSiteN2ESubmit
			: ufoEventNewSiteN2NSubmit;

		if (recaptchaFailedToLoad && recaptchaRetryTimeRemainingSecond <= 0) {
			ufoSubmitEvent.failure({
				customData: {
					reactComponent: 'SiteActivationContextProvider',
					analyticsEvent: 'errored',
					recaptchaRetryTimeRemainingSecond: 0,
					siteType: showSitePicker ? 'existing-site' : 'new-site',
				},
				error: new Error('Recaptcha retry failed'),
			});
		}
		if (recaptchaFailedToLoad) {
			activateSiteHandler()
				.then(() => {
					ufoSubmitEvent.success({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'succeeded',
							siteType: showSitePicker ? 'existing-site' : 'new-site',
							recaptchaRetryTimeRemainingSecond,
						},
					});
				})
				.catch((error) => {
					showRecaptchaFormError();
					ufoSubmitEvent.failure({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'errored',
							siteType: showSitePicker ? 'existing-site' : 'new-site',
							recaptchaRetryTimeRemainingSecond,
						},
						error,
					});
					return;
				});
		}
	}, [
		activateSiteHandler,
		executeRecaptcha,
		localizedContactUrl,
		recaptchaFailedToLoad,
		recaptchaRetryTimeRemainingSecond,
		showRecaptchaFormError,
		showSitePicker,
	]);

	useEffect(() => {
		if (!recaptchaFailedToLoad) return;
		if (recaptchaRetryTimeRemainingSecond <= 0) return;
		const intervalId = setInterval(() => {
			setRecaptchaRetryTimeRemainingSecond(recaptchaRetryTimeRemainingSecond - 1);
		}, 1000);
		return () => clearInterval(intervalId);
	}, [recaptchaFailedToLoad, recaptchaRetryTimeRemainingSecond]);

	const activateSite = async ({
		privacyPolicy,
	}: {
		privacyPolicy?: string;
		// eslint-disable-next-line complexity
	}) => {
		setHasSubmitted(true);
		sessionStorage.setItem('provisioningStartTime', `${Date.now()}`);
		// Start UFOs to begin duration metrics for signup submit
		ufoEventNewSiteN2NSubmit.start({
			productKey,
			webPlatform: 'site-activation',
		});
		ufoEventExistingSiteN2ESubmit.start({
			productKey,
			webPlatform: 'site-activation',
		});
		const ufoLogs: UfoLabelStack = [...ufoBaseLogs, { name: 'method:activateSite' }];

		// user must be logged in
		if (!user.isUserLoggedIn) return;

		// Generate a signup ID and add it to all the events in this flow.
		// This id is used to stitch together a view of the end-to-end
		// provisioning experience, all the way to landing in product.
		const signupId = createUUID();
		const isN2N = !showSitePicker && !sitePickerValue;
		const domainType = profile?.cohorts?.domainType;
		trackSignupStarted(createAnalyticsEvent, signupId, productKey, edition, isN2N, domainType);

		// handle site picker submit
		if (showSitePicker) {
			if (sitePickerValue) {
				const upgradableProduct = sitePickerValue.products.find(
					(product) =>
						[product.productKey, product.partOfCollection].includes(productKey) &&
						product.productEdition === 'free',
				);

				// checking the standard-to-premium upgrade eligibility cohort
				const hasUpgradableStandardProduct =
					edition === 'premium' &&
					[
						ProductKey.JIRA_SOFTWARE,
						ProductKey.CONFLUENCE,
						ProductKey.JIRA_PRODUCT_DISCOVERY,
					].includes(productKey) &&
					sitePickerValue.products.some(
						(product) =>
							[product.productKey].includes(productKey) && product.productEdition === 'standard',
					);

				if (upgradableProduct || hasUpgradableStandardProduct) {
					// send to adminhub
					const redirectUrl = await getAdminHubUpgradeProductUrl(
						sitePickerValue,
						productKey,
						edition,
					);

					createAnalyticsEvent({
						eventName: 'signupUpgrade',
						eventComponent: 'button',
						billingSystem: sitePickerValue.products[0]?.billingSourceSystem,
						cloudId: sitePickerValue.cloudId,
						event: 'clicked',
						eventType: 'ui',
						action: 'upgrade',
					}).fire('signup');

					window.location.assign(redirectUrl);
					return;
				}

				try {
					// Check for Shadow IT authorization.
					await stargateClient.getActivationAuthorization({
						productKeys: [productKey],
						productEdition: edition as string,
						cloudId: sitePickerValue.cloudId,
						siteName: sitePickerValue?.displayName,
					});

					const searchParameters = new URLSearchParams(window.location.search);
					const signupSource = searchParameters.get('signupSource');
					// Fetch the activation plan once the user has clicked so redirect
					// handling can preserve paid-target confirmation flows.
					const hasPaidTargetEditionRequiringConfirmationScreen =
						await getHasPaidTargetEditionRequiringConfirmationScreen({
							cloudId: sitePickerValue.cloudId,
							productKey,
							stargateClient,
							signupSource,
							createAnalyticsEvent,
						});

					const crossFlowActivationUrl = getCrossFlowActivationUrl({
						cloudId: sitePickerValue.cloudId,
						edition,
						productKey: primaryCollectionProduct ?? productKey,
						siteName: sitePickerValue?.displayName,
						hasPaidTargetEditionRequiringConfirmationScreen,
					});

					ufoEventExistingSiteN2ESubmit.success({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'succeeded',
							siteType: 'existing-site',
						},
					});

					trackSitePickerSubmit({
						createAnalyticsEvent,
						targetCloudId: sitePickerValue.cloudId,
						provisionedProducts: [{ product: productKey, edition }],
						signupUrl: crossFlowActivationUrl,
						aaid: user['accountId'],
						signupSource,
						componentSource: 'SiteActivationContextProvider',
						hasPaidTargetEditionRequiringConfirmationScreen,
					});

					window.location.assign(crossFlowActivationUrl);
				} catch (error) {
					ufoEventExistingSiteN2ESubmit.failure({
						customData: {
							reactComponent: 'SiteActivationContextProvider',
							analyticsEvent: 'errored',
							siteType: 'existing-site',
						},
						error,
					});

					redirectToAdminHubForShadowIt(error);
				}
			}
			return;
		}

		if (isValidating) {
			const { availability } = await stargateClient.checkCloudNameAvailability({
				cloudName: siteName,
			});

			if (availability !== 'AVAILABLE') {
				setHasSubmitted(false); // reset to allow re-submit
				return;
			}

			setSiteNameValid(true);
		}

		if ((!siteNameValid && !isValidating) || !siteName) {
			setHasSubmitted(false); // reset to allow re-submit
			return;
		}

		trackSubmitSuccess(createAnalyticsEvent);

		if (siteName !== recommendedSiteName) {
			trackSiteNameChangedFromRecommendedSiteName(
				createAnalyticsEvent,
				siteName,
				recommendedSiteName,
			);
		}

		let setTimeoutId: ReturnType<typeof setTimeout>;

		const interactionId = createUUID();
		operateWithSignupUFOLogging(
			{
				interactionId,
				interactionName: 'try-cloud-signup_form-submit',
				type: 'press',
				timeout: 2 * 60 * 1000,
				labelStack: [{ name: 'uip' }],
				errorContext: 'error occured on signup form submit button',
			},
			async () =>
				Promise.race([
					new Promise((_resolve, reject) => {
						setTimeoutId = setTimeout(() => {
							reject(SignupUFOLoggingActionTimeoutError);
						}, 60000);
					}),
					activateSiteHandler(signupId, privacyPolicy).finally(() => {
						if (setTimeoutId) {
							clearTimeout(setTimeoutId);
						}
					}),
				]),
		)
			.then(() => {
				// SLO "good event" for N2N signup submit
				ufoEventNewSiteN2NSubmit.success({
					customData: {
						reactComponent: 'SiteActivationContextProvider',
						analyticsEvent: 'succeeded',
						siteType: 'new-site',
					},
				});

				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.ACTIVATE_PRODUCT_API_SUCCESS,
					experienceStatus: SUCCEEDED,
					interactionType: TRANSITION,
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "good event" for signup submit captured on bxp-ui only
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_SUCCEEDED,
					experienceStatus: SUCCEEDED,
					interactionType: PRESS,
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "good event" for signup submit captured across bxp-ui and bxpkit
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_SUCCEEDED,
					experienceStatus: SUCCEEDED,
					interactionType: PRESS,
					ufoLogger: ufoLogs,
				});
			})
			.catch((error) => {
				// Show add-payment-method page if Rovo Dev 403 (user clicks "Open payment methods" to open in new tab)
				if (productKey === ProductKey.DEVAI && error?.response?.status === 403) {
					const adminPaymentLink = getAdminPaymentLink(error);
					if (adminPaymentLink) {
						setIsSubmitting(false);
						setHasSubmitted(false);
						setPaymentDetailsView({
							view: 'payment-required',
							adminPaymentLink,
						});
						return;
					}
				}

				// SLO "bad event" for N2N signup submit
				ufoEventNewSiteN2NSubmit.failure({
					customData: {
						reactComponent: 'SiteActivationContextProvider',
						analyticsEvent: 'errored',
						siteType: 'new-site',
					},
					error,
				});

				redirectToAdminHubForShadowIt(error);

				const traceId = error?.response?.headers?.get('atl-traceid');
				setValidationMessage({
					...validationMessages.timeout,
					values: {
						// eslint-disable-next-line react/no-unstable-nested-components
						'href-support': (node: ReactNode[]) => <a href={localizedContactUrl}>{node}</a>,
						error_id: traceId ? `Trace ID: ${traceId}` : `Interaction ID: ${interactionId}`,
					},
				});

				// don't re-enable the form when there is an HTTP exception. this helps with rate-limiting
				const errorType = SignupGetUFOErrorType(error);
				if (errorType !== 'http_exception') {
					// Reset form to allow re-submit
					setIsSubmitting(false);
					setHasSubmitted(false);
				}

				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.ACTIVATE_PRODUCT_API_ERROR,
					experienceStatus: ABORTED,
					interactionType: TRANSITION,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "bad event" for signup submit captured on bxp-ui only
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.BXPUI_SIGNUP_SUBMIT_SLO_FAILED,
					experienceStatus: ABORTED,
					interactionType: PRESS,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});

				// Reliabiltiy SLO "bad event" for signup submit captured across bxp-ui and bxpkit
				trackAndCompleteUFOEvent({
					experienceName: UfoEventName.DEFAULT_SIGNUP_SUBMIT_SLO_FAILED,
					experienceStatus: ABORTED,
					interactionType: PRESS,
					error,
					errorName: 'ActivateProductError',
					errorMessage: 'error occured on activateProduct',
					ufoLogger: ufoLogs,
				});
			});
	};

	return (
		<SiteActivationContext.Provider
			value={{
				isSubmitting,
				activateSite,
				showSitePicker,
				sites,
				isRequestInProgress: profile.requestInProgress,
				email: user['email'],
				sitePickerValue,
				setSitePickerValue,
				setSiteNameValid,
				siteName,
				setSiteNameValue,
				setShowSitePicker,
				recommendedSiteName,
				hasSubmitted,
				setHasSubmitted,
				validationMessageState: [validatationMessage, setValidationMessage],
				isValid,
				isValidating,
				error,
				isWorkspaceProduct,
				selectedTransactionAccountId,
				setSelectedTransactionAccountId,
				primaryCollectionProduct,
				paymentDetailsView,
				setPaymentDetailsView,
			}}
		>
			{children}
		</SiteActivationContext.Provider>
	);
};
